import React, { useEffect, useImperativeHandle, useRef, useState, forwardRef } from 'react';
import Quill from 'quill';
import 'quill/dist/quill.snow.css';
import QuillCursors from 'quill-cursors';
import BlotFormatter from '@enzedonline/quill-blot-formatter2';
import '../../utils/signEditorImages'; // registers the signed-image Quill blot
import AiActionToolbar from '../../components/AiActionToolbar';
import {
  registerPendingEditBlot,
  insertPendingEdits as insertPendingEditsHelper,
  attachPendingEditControls,
} from './process/pendingEditBlot';
import {
  registerIcdProblemBlot,
  attachIcdProblemControls,
} from './process/icdProblemBlot';
import { registerTaskLink } from './taskLinkBlot';

// Remote-cursor rendering for live collab. The `cursors` module draws a thin
// caret + name flag at the position of each peer's cursor; y-quill's
// QuillBinding pumps awareness updates into it automatically when the binding
// is constructed with a provider's awareness object. Registration is
// idempotent so this is safe on hot reload.
Quill.register('modules/cursors', QuillCursors);
Quill.register('modules/blotFormatter', BlotFormatter);

// ─── Custom Quill setup ──────────────────────────────────────────────────────
// These registrations run ONCE at module load (Quill.register is idempotent).
// Without them the NP template's class-based color attributes and
// `vitals-grid` embed would be silently dropped by Quill.

// Class-based color attributor: render colors as `<span class="ql-color-X">`
// instead of inline `style="color:..."`. Lets the dark-mode CSS in clinic.css
// brighten section accents (blue/orange/teal/...) the same way design.html
// does with `text-blue-600 dark:text-blue-400`.
const ColorClass = Quill.import('attributors/class/color');
ColorClass.whitelist = ['blue', 'orange', 'teal', 'purple', 'red', 'green', 'emerald', 'slate', 'amber'];
Quill.register(ColorClass, true);

const BlockEmbed = Quill.import('blots/block/embed');

// design.html vitals row: 6 boxes, each with a bold mono value and a small
// uppercase label. Mirrors the markup at backend/design.html:316-343 exactly.
const VITAL_CELLS = [
  { key: 'weight', label: 'WEIGHT (LB)', color: 'orange' },
  { key: 'bmi',    label: 'BMI',         color: 'orange' },
  { key: 'height', label: 'HEIGHT',      color: 'slate' },
  { key: 'bp',     label: 'BP (MMHG)',   color: 'emerald' },
  { key: 'hr',     label: 'HR (BPM)',    color: 'emerald' },
  { key: 'spo2',   label: 'SPO₂',   color: 'emerald' },
];

const VITAL_BOX_BG = {
  orange:  'bg-orange-50 dark:bg-orange-500/[0.07] border-orange-200/70 dark:border-orange-500/25',
  emerald: 'bg-emerald-50 dark:bg-emerald-500/[0.07] border-emerald-200/70 dark:border-emerald-500/25',
  slate:   'bg-white dark:bg-slate-800/30 border-slate-200 dark:border-slate-700/50',
};
const VITAL_TEXT = {
  orange:  'text-orange-700 dark:text-orange-300',
  emerald: 'text-emerald-700 dark:text-emerald-300',
  slate:   'text-slate-700 dark:text-slate-200',
};

function escapeAttr(s) {
  return String(s).replace(/[&<>"']/g, (c) => ({ '&': '&amp;', '<': '&lt;', '>': '&gt;', '"': '&quot;', "'": '&#39;' }[c]));
}

// Tab-to-next-blank helper. Templates use "___" (3+ underscores) as
// fill-me-in markers. Find the next/prev run of underscores from the
// caret, select it so the next keystroke overtypes the blank, and
// return false so Quill's default Tab handler (insert tab) is skipped.
const BLANK_RE = /_{3,}/g;
function jumpToBlank(quill, direction) {
  const text = quill.getText();
  const sel = quill.getSelection(true);
  const cursor = sel ? sel.index : 0;
  const matches = [];
  let m;
  // Reset lastIndex defensively; module-level regex state shouldn't leak
  // but be safe across rapid keypresses.
  BLANK_RE.lastIndex = 0;
  while ((m = BLANK_RE.exec(text)) !== null) {
    matches.push({ index: m.index, length: m[0].length });
    if (m.index === BLANK_RE.lastIndex) BLANK_RE.lastIndex += 1; // zero-width safety
  }
  if (matches.length === 0) return true; // no blanks → let Tab do its default
  let target;
  if (direction >= 0) {
    target = matches.find((b) => b.index > cursor) || matches[0]; // wrap
  } else {
    const before = matches.filter((b) => b.index + b.length <= cursor);
    target = before.length ? before[before.length - 1] : matches[matches.length - 1]; // wrap
  }
  quill.setSelection(target.index, target.length, 'user');
  return false;
}

function buildVitalsGridHTML(value) {
  const v = (value && typeof value === 'object') ? value : {};
  const cellsHTML = VITAL_CELLS.map((c) => {
    const bg = VITAL_BOX_BG[c.color];
    const txt = VITAL_TEXT[c.color];
    // Stored value may be a plain string (legacy: numeric reading only) OR
    // an object `{ value, label }` once the label has been edited. Accept both.
    const cellState = (v[c.key] && typeof v[c.key] === 'object') ? v[c.key] : { value: v[c.key] || '', label: undefined };
    const val = escapeAttr(cellState.value || '');
    const labelText = escapeAttr(cellState.label != null ? cellState.label : c.label);
    // No whitespace between tags — newlines/indentation form anonymous
    // inline boxes inside the cell that take a full line-height each (~24px
    // with the editor's line-height: 1.625), inflating each box by ~70px
    // even though the only real content is a 28px input + 15px label.
    return `<div class="rounded-xl border ${bg} px-3 py-3 text-center"><input type="text" data-vital="${c.key}" value="${val}" placeholder="___" class="vital-cell font-mono font-bold text-xl ${txt} bg-transparent text-center w-full outline-none border-none p-0 m-0" /><div contenteditable="true" data-vital-label="${c.key}" class="text-[9px] tracking-widest font-semibold text-slate-500 dark:text-slate-400 mt-1 outline-none">${labelText}</div></div>`;
  }).join('');
  return `<div class="grid grid-cols-6 gap-2 my-3">${cellsHTML}</div>`;
}

function readVitalsValues(node) {
  const value = {};
  node.querySelectorAll('input[data-vital]').forEach((input) => {
    const key = input.dataset.vital;
    value[key] = { value: input.value, label: value[key]?.label };
  });
  node.querySelectorAll('[data-vital-label]').forEach((el) => {
    const key = el.dataset.vitalLabel;
    const prev = value[key] || { value: '' };
    value[key] = { ...prev, label: el.textContent };
  });
  return value;
}

// design.html review-of-systems grid: 2 columns × 12 cards. Default cards are
// slate/white; positive findings (CARD, RESP in the mockup) are red. The
// first six default to the design.html mockup verbatim; the remaining six
// (SKIN…ALLERGIC/IMMUNOLOGIC) cover the doctor NP template's full 12-ROS
// review so the medstudent NP has parity. All descriptions are editable.
const ROS_CARDS = [
  { key: 'gen',   label: 'GEN',   positive: false, description: 'Fever, fatigue, weakness, weight gain or loss.' },
  { key: 'heent', label: 'HEENT', positive: false, description: 'Vision changes, dryness, loss of taste/smell, post nasal drip, sore tongue, sores in the mouth, frequent sore throats, hoarseness, waking up with acid in throat, food sticking/painful swallowing.' },
  { key: 'card',  label: 'CARD',  positive: true,  description: 'Chest pain, palpitations, murmurs, SOB, difficulty breathing at night, swollen legs, claudication.' },
  { key: 'resp',  label: 'RESP',  positive: true,  description: 'Cough, hemoptysis, sputum, wheezing. Night gasping/choking or sweats, snoring.' },
  { key: 'gi',    label: 'GI',    positive: false, description: 'Decreased appetite, nausea or vomiting, heartburn, pain, jaundice, diarrhea, constipation, blood in stools.' },
  { key: 'msk',   label: 'MSK',   positive: false, description: 'Cramps, joint/muscle pain or swelling, weakness, tenderness, neck/back pain.' },
  { key: 'skin',  label: 'SKIN',  positive: false, description: 'Redness, rash, hives, photosensitivity, tightness, nodules/bumps, hair loss, fingers changing colors.' },
  { key: 'neuro', label: 'NEURO', positive: false, description: 'Headache, dizziness, memory loss, seizures, tremors, extremity numbness or weakness.' },
  { key: 'psych', label: 'PSYCH', positive: false, description: 'Depression, mania, hallucinations/psychosis, anxiety, obsessions and compulsions, trauma history, body image disturbances, eating disorders, or pain disorders.' },
  { key: 'endo',  label: 'ENDO',  positive: false, description: 'Flushing, fingernail changes, excessive thirst or urination, heat or cold intolerance, irregular menses.' },
  { key: 'heme',  label: 'HEME',  positive: false, description: 'Abnormal bruising or bleeding, clotting tendency, lymph node swelling.' },
  { key: 'allerg',label: 'ALLERG',positive: false, description: 'Immunocompromised, skin sensitivity, sneezing, itching or swelling with allergies.' },
];

function buildRosGridHTML(value) {
  const v = (value && typeof value === 'object') ? value : {};
  const cardsHTML = ROS_CARDS.map((c) => {
    const stored = v[c.key];
    const description = stored?.description ?? c.description;
    const isPositive = stored?.positive ?? c.positive;
    // `stored.label` is what we read back if the user has renamed the label
    // (e.g. HEENT → "Head/Neck"). Falls back to the template default.
    const labelText = stored?.label != null ? stored.label : c.label;
    const cardBg = isPositive
      ? 'bg-red-50 dark:bg-red-500/[0.06] border-red-200 dark:border-red-500/25'
      : 'bg-white dark:bg-slate-800/30 border-slate-200 dark:border-slate-700/50';
    const labelColor = isPositive
      ? 'text-red-500 dark:text-red-400'
      : 'text-slate-400 dark:text-slate-500';
    const descColor = isPositive
      ? 'text-slate-700 dark:text-slate-200'
      : 'text-slate-600 dark:text-slate-300';
    // No whitespace between tags — same anonymous-line-box bloat as the
    // vitals grid would inflate each card otherwise.
    // Both the label and the description are contenteditable so users can
    // rename HEENT, CARD, etc. The label carries `data-ros-label="<key>"`
    // so the input listener can re-read it on every keystroke.
    return `<div class="rounded-lg border ${cardBg} px-3 py-2 flex gap-3" data-ros-positive="${isPositive}"><div class="text-[10px] tracking-widest font-bold ${labelColor} w-12 shrink-0 mt-0.5 ros-card-label outline-none" data-ros-label="${c.key}" contenteditable="true">${escapeAttr(labelText)}</div><div class="text-[13px] ${descColor} ros-card-desc outline-none" data-ros="${c.key}" contenteditable="true">${escapeAttr(description)}</div></div>`;
  }).join('');
  return `<div class="grid grid-cols-2 gap-2 my-3">${cardsHTML}</div>`;
}

function readRosValues(node) {
  const value = {};
  node.querySelectorAll('.ros-card-desc').forEach((el) => {
    const key = el.dataset.ros;
    const card = el.closest('[data-ros-positive]');
    value[key] = {
      ...(value[key] || {}),
      description: el.textContent,
      positive: card?.getAttribute('data-ros-positive') === 'true',
    };
  });
  node.querySelectorAll('.ros-card-label').forEach((el) => {
    const key = el.dataset.rosLabel;
    value[key] = { ...(value[key] || {}), label: el.textContent };
  });
  return value;
}

class RosGridBlot extends BlockEmbed {
  static create(value) {
    const node = super.create();
    node.setAttribute('contenteditable', 'false');
    node.innerHTML = buildRosGridHTML(value);
    return node;
  }
  static value(node) { return readRosValues(node); }
}
RosGridBlot.blotName = 'ros-grid';
RosGridBlot.tagName = 'div';
RosGridBlot.className = 'ql-ros-grid';
Quill.register(RosGridBlot);

// Returns a Delta with every embed op's value re-read from its current DOM
// (input values for vitals, contenteditable text for ROS cards). Used at the
// point of save because Quill's internal delta doesn't track input value
// changes or text mutations inside contenteditable=false embeds — those
// happen below its MutationObserver.
function getFreshDeltaWithEmbeds(quill) {
  const delta = quill.getContents();
  const vitalsWrappers = quill.root.querySelectorAll('.ql-vitals-grid');
  const rosWrappers = quill.root.querySelectorAll('.ql-ros-grid');
  let vIdx = 0;
  let rIdx = 0;
  delta.ops.forEach((op) => {
    if (op.insert && typeof op.insert === 'object') {
      if (Object.prototype.hasOwnProperty.call(op.insert, 'vitals-grid')) {
        const w = vitalsWrappers[vIdx]; vIdx += 1;
        if (w) op.insert['vitals-grid'] = readVitalsValues(w);
      }
      if (Object.prototype.hasOwnProperty.call(op.insert, 'ros-grid')) {
        const w = rosWrappers[rIdx]; rIdx += 1;
        if (w) op.insert['ros-grid'] = readRosValues(w);
      }
    }
  });
  return delta;
}

// BlockEmbed: Quill treats this as a single atomic block. Cursor moves AROUND
// it, never INTO it; the <input> children handle their own focus and editing.
// `create(value)` builds the DOM from the value; `value(node)` reads back.
// To get changes back into Quill's internal delta we wire input listeners
// after Quill mounts (see editor setup below).
class VitalsGridBlot extends BlockEmbed {
  static create(value) {
    const node = super.create();
    node.setAttribute('contenteditable', 'false');
    node.innerHTML = buildVitalsGridHTML(value);
    return node;
  }
  static value(node) {
    return readVitalsValues(node);
  }
}
VitalsGridBlot.blotName = 'vitals-grid';
VitalsGridBlot.tagName = 'div';
VitalsGridBlot.className = 'ql-vitals-grid';
Quill.register(VitalsGridBlot);

// Stage 2 keystone — pending-edit blot for inline Process Forms diffs.
// Registration is lazy + idempotent; calling here ensures the class is wired
// before the editor mounts so suggested edits can be inserted from anywhere.
registerPendingEditBlot();

// ICD-10 problem blot (Task #38). Renders numbered A&P problems as an
// interactive bold line with an ICD-10 search dropdown. Same registration
// pattern as the pending-edit blot above — lazy + idempotent.
registerIcdProblemBlot();

// Task links (note text assigned as a task — see taskLinkBlot.js). Styled here
// rather than in clinic.css so the format and its look ship together; the
// click → TaskLinkCard wiring lives in MedStudentClinicPage.
registerTaskLink(Quill);

const TASK_LINK_CSS = `
.ql-editor a.ql-task-link { color: #2563eb; text-decoration: underline dotted; text-underline-offset: 3px; cursor: pointer; }
.ql-editor a.ql-task-link::after { content: "📋"; font-size: 0.75em; margin-left: 2px; }
.dark .ql-editor a.ql-task-link { color: #60a5fa; }
/* Patients / read-only viewers (container class set by attachTaskLinkClicks): plain text. */
.ql-task-links-inert .ql-editor a.ql-task-link,
.dark .ql-task-links-inert .ql-editor a.ql-task-link { color: inherit; text-decoration: none; cursor: text; }
.ql-task-links-inert .ql-editor a.ql-task-link::after { content: none; }
@media print { .ql-editor a.ql-task-link::after { content: none; } }
`;
if (typeof document !== 'undefined' && !document.getElementById('ql-task-link-style')) {
  const style = document.createElement('style');
  style.id = 'ql-task-link-style';
  style.textContent = TASK_LINK_CSS;
  document.head.appendChild(style);
}

// ─── Component ───────────────────────────────────────────────────────────────

// Walk the editor DOM and lock/unlock every embed interactive so vitals
// inputs and ROS contenteditable children agree with Quill's enabled state.
// Native form inputs ignore the parent `contenteditable=false`, so without
// this they'd stay typable even when the editor is in read mode.
function setEmbedsReadOnly(quill, readOnly) {
  if (!quill || !quill.root) return;
  quill.root.querySelectorAll('input[data-vital]').forEach((el) => {
    if (readOnly) el.setAttribute('readonly', 'readonly');
    else el.removeAttribute('readonly');
  });
  quill.root.querySelectorAll('[data-vital-label],[data-ros-label],[data-ros]').forEach((el) => {
    el.setAttribute('contenteditable', readOnly ? 'false' : 'true');
  });
}

const DocumentBody = forwardRef(function DocumentBody(
  { document: doc, title, subtitle, readOnly = false, onChange, bindingActive = false },
  ref,
) {
  const quillHostRef = useRef(null);
  const quillRef = useRef(null);
  // Track the document we last loaded into Quill so we don't reset cursor on every render
  const lastLoadedDocRef = useRef(null);
  // Keep latest onChange in a ref so the text-change listener stays stable
  const onChangeRef = useRef(onChange);
  useEffect(() => { onChangeRef.current = onChange; }, [onChange]);

  useEffect(() => {
    if (!quillHostRef.current) return;

    // If the host element changed since we last mounted Quill (e.g., the
    // component remounted after a transient `doc === null` during patient
    // switch), the previous Quill instance is now attached to a detached node.
    // Recreate it onto the live host.
    if (quillRef.current && quillRef.current.container !== quillHostRef.current.firstChild &&
        !quillHostRef.current.contains(quillRef.current.container)) {
      quillRef.current = null;
      lastLoadedDocRef.current = null;
    }

    // Mount Quill once and register the text-change listener once
    if (!quillRef.current) {
      const quill = new Quill(quillHostRef.current, {
        theme: 'snow',
        modules: {
          toolbar: false,
          blotFormatter: {},
          // `cursors` module is harmless when no binding is attached (no remote
          // cursors → nothing rendered). We enable it unconditionally so
          // toggling the collab flag doesn't require a Quill remount.
          cursors: {
            hideDelayMs: 5000,
            hideSpeedMs: 200,
            transformOnTextChange: true,
          },
          // Leejoe 2026-06-29 — explicit history config so undo/redo can be
          // surfaced as toolbar buttons (Google Docs-style ↶ ↷ arrows next
          // to Restore). `userOnly: true` is critical with the collab y-quill
          // binding active: without it, every remote sync would push onto
          // the local undo stack and one Ctrl+Z would undo someone else's
          // edit. `delay: 1000` groups a stream of typing into a single
          // undo step so the user doesn't have to mash Ctrl+Z per keystroke.
          history: {
            delay: 1000,
            maxStack: 200,
            userOnly: true,
          },
          // Hotkey: Tab cycles to the next "___" blank and selects it so
          // the doctor can immediately overtype. Shift+Tab goes backward.
          // Cmd/Ctrl+Tab is left to the browser. The handler always returns
          // false so Quill's default Tab (insert tab char) never fires.
          keyboard: {
            bindings: {
              tabToNextBlank: {
                key: 'Tab',
                handler: function () {
                  return jumpToBlank(this.quill, +1);
                },
              },
              tabToPrevBlank: {
                key: 'Tab',
                shiftKey: true,
                handler: function () {
                  return jumpToBlank(this.quill, -1);
                },
              },
              // Ctrl/Cmd+Shift+X → toggle strikethrough on the current
              // selection. Quill matches bindings by exact `evt.key`, but
              // browsers disagree on the case reported when Cmd/Ctrl+Shift
              // is held with a letter (Chrome on macOS has flipped between
              // 'X' and 'x' across releases). Registering both keys plus
              // the key code makes this fire regardless.
              strikethroughToggle: {
                key: ['X', 'x', 88],
                shortKey: true, // Ctrl on Win/Linux, Cmd on macOS
                shiftKey: true,
                handler: function (range, context) {
                  this.quill.format('strike', !context.format.strike, 'user');
                  return false;
                },
              },
            },
          },
        },
      });
      quillRef.current = quill;

      // Stage 2 keystone — wire the pending-edit ✓ / × / ✎ buttons. Event
      // delegation on the editor root so blots inserted later still work.
      // The detach fn is cleaned up in the unmount return below alongside
      // the binding teardown.
      const detachPendingEditClicks = attachPendingEditControls(quill, {
        onEdit: (editId, card) => {
          // v1 of the spike: just log + outline the card so we can confirm
          // the handler fires. The full inline textarea ride lands in the
          // wired ProcessModal hand-off later in Stage 2.
          if (card) card.classList.add('ring-2', 'ring-amber-400');
          // eslint-disable-next-line no-console
          console.log('[pending-edit] edit clicked', editId);
        },
      });
      quill.__detachPendingEditClicks = detachPendingEditClicks;

      // ICD-10 problem blot — wire pencil click / suggestion pick / keyboard
      // nav. Event delegation on the editor root, same pattern as
      // pending-edit. Idempotent; safe across hot reload.
      attachIcdProblemControls(quill);

      // Window-exposed spike helper. Lets us verify the blot round-trips
      // without the full ProcessModal hand-off (Stage 2 wiring). Usage from
      // DevTools console on a patient page:
      //
      //   window.__vmedTestPendingEdits([{
      //     id: 'e1', kind: 'addition', section: 'HPI',
      //     anchor: 'HISTORY OF PRESENT ILLNESS',
      //     newText: '45 yo with progressive fatigue x 3 weeks.',
      //     source: { label: 'spike test' },
      //     rationale: 'verifying inline blot',
      //   }])
      //
      // To accept / reject the inserted edits, click the ✓ / × buttons on
      // the rendered cards.
      if (typeof window !== 'undefined') {
        window.__vmedTestPendingEdits = (edits) => {
          try {
            return insertPendingEditsHelper(quill, edits);
          } catch (e) {
            console.error('[pending-edit] spike insert failed', e);
            return [];
          }
        };
      }

      // ── Clipboard paste sanitizer (Leejoe's Issue #3) ────────────────
      // Strip dark-background/color styling from pasted content so text
      // copied from the Copilot recorder window (which uses a black
      // background + light font) lands in the chart note using the
      // note's own font + theme. Without this, every paste needed manual
      // formatting cleanup.
      try {
        const clipboard = quill.getModule('clipboard');
        if (clipboard && typeof clipboard.addMatcher === 'function') {
          clipboard.addMatcher(Node.ELEMENT_NODE, (node, delta) => {
            if (node && node.style) {
              node.style.removeProperty('background');
              node.style.removeProperty('background-color');
              node.style.removeProperty('color');
            }
            // Strip the same properties from any nested op attributes the
            // default matcher already emitted into `delta`.
            const ops = (delta && delta.ops) || [];
            ops.forEach((op) => {
              if (op && op.attributes) {
                delete op.attributes.background;
                delete op.attributes.color;
              }
            });
            return delta;
          });
        }
      } catch (err) {
        console.warn('[DocumentBody] clipboard matcher registration failed', err);
      }

      // ── "Make this my note" handler (Leejoe's Issue #2) ──────────────
      // RecordingBubble fans out a CustomEvent when the recorder window
      // sends INSERT_REPORT_AS_NOTE; we receive it here and insert the
      // plain text into the active Quill at the current selection (or
      // at the end if no selection). User can undo with Cmd+Z.
      const onInsertReport = (e) => {
        const plain = e?.detail?.plain || '';
        if (!plain || !quillRef.current) return;
        const q = quillRef.current;
        const sel = q.getSelection(true);
        const index = sel ? sel.index : q.getLength();
        q.insertText(index, plain + '\n', 'user');
        q.setSelection(index + plain.length + 1, 0, 'user');
        q.focus();
        console.log('[DocumentBody] Inserted SOAP report into Quill at index', index);
      };
      window.addEventListener('vmed:insertReportAsNote', onInsertReport);
      // Capture for cleanup
      quill.__vmedInsertReportCleanup = () => {
        window.removeEventListener('vmed:insertReportAsNote', onInsertReport);
      };

      // Cmd/Ctrl+click anywhere inside the editor focuses the section
      // whose header you clicked (or the nearest section above the click).
      // Plain click keeps Quill's default caret-placement behavior.
      const editorRoot = quill.root;
      const onCmdClick = (e) => {
        if (!(e.metaKey || e.ctrlKey)) return;
        // Find the nearest block-level container of the click target.
        const target = e.target instanceof Element ? e.target : null;
        if (!target) return;
        // Block elements Quill emits at the top level under .ql-editor.
        const block = target.closest('h1, h2, h3, h4, h5, h6, p, ol, ul, blockquote, .ql-editor > *');
        if (!block || !editorRoot.contains(block)) return;
        e.preventDefault();
        try {
          const range = document.createRange();
          range.selectNodeContents(block);
          range.collapse(true); // caret at start of section
          const sel = window.getSelection();
          sel.removeAllRanges();
          sel.addRange(range);
          // Sync Quill's internal selection model so subsequent typing works.
          const idx = quill.getIndex
            ? null
            : null;
          // Quill's getSelection(true) updates from the DOM selection.
          quill.focus();
          quill.getSelection(true);
        } catch (_) {
          /* selection failed — fall back to caret at click point (default). */
        }
      };
      editorRoot.addEventListener('click', onCmdClick);
      // Stash for cleanup if DocumentBody unmounts.
      quill.__cmdClickCleanup = () => editorRoot.removeEventListener('click', onCmdClick);

      // Task #281 — NextGen-safe copy. Legacy EHR fields garble typographic
      // Unicode (em dashes → '#', curly apostrophes → mojibake; Noreen
      // 2026-07-07). On copy, ship an ASCII-transliterated text/plain flavor
      // while keeping the rich text/html flavor so Word / Google Docs paste
      // targets keep their formatting.
      const ASCII_MAP = [
        [/[‘’‛′]/g, "'"],
        [/[“”‟″]/g, '"'],
        [/[–—―−]/g, '-'],
        [/[•·‧]/g, '-'],
        [/₂/g, '2'],
        [/°/g, ''],
        [/ /g, ' '],
        [/→/g, '->'],
        [/×/g, 'x'],
        [/…/g, '...'],
      ];
      const toNextGenAscii = (s) => ASCII_MAP.reduce((acc, [re, rep]) => acc.replace(re, rep), s);
      const onCopyAscii = (e) => {
        try {
          const sel = window.getSelection();
          if (!sel || sel.isCollapsed || !e.clipboardData) return;
          const plain = sel.toString();
          if (!plain) return;
          const container = document.createElement('div');
          for (let i = 0; i < sel.rangeCount; i += 1) {
            container.appendChild(sel.getRangeAt(i).cloneContents());
          }
          e.clipboardData.setData('text/html', container.innerHTML);
          e.clipboardData.setData('text/plain', toNextGenAscii(plain));
          e.preventDefault();
        } catch (_) { /* fall back to the browser's default copy */ }
      };
      editorRoot.addEventListener('copy', onCopyAscii);
      quill.__vmedCopyAsciiCleanup = () => editorRoot.removeEventListener('copy', onCopyAscii);

      // quill-cursors v4 has a stale-color bug: `createCursor(id, name, color)`
      // is a no-op when a cursor with that id already exists, so the color is
      // permanently set by the FIRST call. y-quill calls createCursor on every
      // awareness change, including the very first frame where a peer's
      // `user` field may not be populated yet — that initial frame locks the
      // cursor at quill-cursors' default and later frames with the right
      // color are silently ignored. Monkey-patch createCursor so subsequent
      // calls with a different color update the existing cursor's caret +
      // flag inline styles too.
      const cursorsModule = quill.getModule('cursors');
      if (cursorsModule && typeof cursorsModule.createCursor === 'function' && !cursorsModule.__colorPatched) {
        const orig = cursorsModule.createCursor.bind(cursorsModule);
        cursorsModule.createCursor = function patchedCreateCursor(id, name, color) {
          const pre = cursorsModule._cursors && cursorsModule._cursors[id];
          const cursor = orig(id, name, color);
          if (cursor && color && pre) {
            try { cursor.color = color; cursor.name = name; } catch (_) {}
            const root = cursor._el || cursor.el || null;
            if (root) {
              const caret = root.querySelector('.ql-cursor-caret');
              const flag = root.querySelector('.ql-cursor-flag');
              const nameEl = root.querySelector('.ql-cursor-name');
              if (caret) caret.style.backgroundColor = color;
              if (flag) flag.style.backgroundColor = color;
              if (nameEl && typeof name === 'string') nameEl.textContent = name;
            }
          }
          return cursor;
        };
        cursorsModule.__colorPatched = true;
      }

      // Only treat USER edits as document changes. setContents() below fires
      // text-change with source='api', and if we forwarded those we'd push
      // the just-loaded content into pendingDeltaRef and trigger an
      // autosave. During a patient switch that's catastrophic: an in-flight
      // save for the previous patient re-schedules itself in its `finally`
      // and ends up writing the NEW patient's content to the OLD patient's
      // path. Callers that need to mark restored content as dirty (e.g.
      // version restore) set pendingDeltaRef themselves.
      quill.on('text-change', (_delta, _oldDelta, source) => {
        // Only treat USER edits as document changes — y-quill applies remote
        // CRDT updates with source='api'; forwarding those would trigger a
        // .json autosave for every keystroke another client makes.
        if (source !== 'user') return;
        onChangeRef.current?.(getFreshDeltaWithEmbeds(quill));
      });

      // Vitals grid edits: <input> value changes don't fire DOM mutations, so
      // Quill's MutationObserver never sees them and the internal delta stays
      // frozen at whatever value the embed was created with. We can't fix
      // this with updateContents — that swaps out the whole embed's DOM and
      // steals focus mid-typing (the user only sees the first character of
      // each cell, and subsequent keystrokes land in the Quill cursor's
      // previous position).
      //
      // Instead: leave the delta alone while typing. On every input, hand
      // onChange a FRESH delta that reads the current DOM input values into
      // the relevant embed ops. pendingDeltaRef in MedStudentClinicPage gets
      // the up-to-date snapshot, autosave persists it, reload restores it via
      // create(value). Focus never moves.
      quill.root.addEventListener('input', (event) => {
        const target = event.target;
        if (!target || !target.dataset) return;
        // Catches vitals-cell <input> edits, vitals-label contenteditable
        // edits, AND ROS card label/description contenteditable edits.
        // All four bypass Quill's MutationObserver flow, so we hand onChange
        // a fresh delta that re-reads embed DOMs.
        const isEmbedEdit =
          target.dataset.vital ||
          target.dataset.vitalLabel ||
          target.dataset.ros ||
          target.dataset.rosLabel;
        if (!isEmbedEdit) return;
        onChangeRef.current?.(getFreshDeltaWithEmbeds(quill));
      });

      // Stop pointer + key events on embed interactives from bubbling to
      // Quill. Without this, Quill's selection handler runs after the click,
      // calls editor.setSelection() with a position outside the embed (since
      // the embed root is contenteditable=false and Quill places its cursor
      // adjacent), and the input/contenteditable child loses focus mid-edit.
      // This is the standard pattern for keeping native inputs inside a
      // Quill BlockEmbed actually editable.
      const isEmbedInteractive = (el) => {
        if (!el || !el.dataset) return false;
        return !!(el.dataset.vital || el.dataset.vitalLabel ||
                  el.dataset.ros || el.dataset.rosLabel);
      };
      ['mousedown', 'mouseup', 'click', 'keydown', 'keyup', 'keypress'].forEach((evt) => {
        quill.root.addEventListener(evt, (event) => {
          if (isEmbedInteractive(event.target)) event.stopPropagation();
        }, true /* capture, so we run before Quill's own handler */);
      });

      // Show blot-formatter resize handles on image hover; hide on mouse-leave.
      // Mirrors the behaviour in EditableDocumentPage so resize works the same way.
      let hoverImgTarget = null;
      let overlayLeaveCleanup = null;
      let isResizingImg = false;
      const detachOverlayLeave = () => {
        if (overlayLeaveCleanup) { overlayLeaveCleanup(); overlayLeaveCleanup = null; }
      };
      const attachOverlayLeaveDismiss = () => {
        detachOverlayLeave();
        const overlay = quill.container.querySelector('.blot-formatter__overlay');
        if (!overlay) return;
        const onLeave = () => {
          if (isResizingImg) return;
          const fmt = quill.getModule('blotFormatter');
          if (fmt && typeof fmt.hide === 'function') fmt.hide();
          hoverImgTarget = null;
          detachOverlayLeave();
        };
        overlay.addEventListener('mouseleave', onLeave);
        overlayLeaveCleanup = () => overlay.removeEventListener('mouseleave', onLeave);
      };
      const onImgMouseOver = (e) => {
        if (isResizingImg || e.buttons) return;
        const t = e.target;
        if (!t || t.tagName !== 'IMG') return;
        if (hoverImgTarget === t) return;
        hoverImgTarget = t;
        t.click();
        attachOverlayLeaveDismiss();
      };
      const onResizePointerDown = (e) => {
        if (e.target?.classList?.contains('blot-formatter__resize-handle')) isResizingImg = true;
      };
      const onResizePointerUp = () => {
        if (!isResizingImg) return;
        isResizingImg = false;
        const overlay = quill.container.querySelector('.blot-formatter__overlay');
        if (!overlay || !overlay.matches(':hover')) {
          const fmt = quill.getModule('blotFormatter');
          if (fmt && typeof fmt.hide === 'function') fmt.hide();
          hoverImgTarget = null;
          detachOverlayLeave();
        }
        // Flush Quill's delta so the new width attribute is captured and onChange fires.
        setTimeout(() => {
          try { quill.update(); } catch (_) { /* ignore */ }
          onChangeRef.current?.(getFreshDeltaWithEmbeds(quill));
        }, 0);
      };
      quill.root.addEventListener('mouseover', onImgMouseOver);
      document.addEventListener('pointerdown', onResizePointerDown, true);
      document.addEventListener('pointerup', onResizePointerUp, true);
      // Stash cleanup so the outer effect return can remove them.
      quill.__imgResizeCleanup = () => {
        quill.root.removeEventListener('mouseover', onImgMouseOver);
        document.removeEventListener('pointerdown', onResizePointerDown, true);
        document.removeEventListener('pointerup', onResizePointerUp, true);
        detachOverlayLeave();
      };
    }

    // Push contents only when the document IDENTITY changes — otherwise we
    // reset the cursor on every parent re-render that produces a fresh object.
    // When the Yjs binding is active, the parent owns Quill's content (every
    // YText change writes through y-quill); setContents here would clobber
    // the live document with a stale prop snapshot.
    if (!bindingActive
        && doc && doc !== lastLoadedDocRef.current
        && doc.delta && Array.isArray(doc.delta.ops)) {
      lastLoadedDocRef.current = doc;
      quillRef.current.setContents(doc.delta);
      // setContents re-creates the embed DOM (vitals/ROS), wiping any
      // readonly/contenteditable attributes we set previously. Reapply.
      setEmbedsReadOnly(quillRef.current, readOnly);
    }
    return () => {
      quillRef.current?.__imgResizeCleanup?.();
    };
  }, [doc, readOnly, bindingActive]);

  // Toggle Quill's edit state. `enable(false)` puts the editor in read-only
  // mode (text can still be selected → copy works; typing/format changes are
  // ignored). Also locks every embed interactive so users can't bypass via
  // the native input.
  useEffect(() => {
    const quill = quillRef.current;
    if (!quill) return;
    quill.enable(!readOnly);
    setEmbedsReadOnly(quill, readOnly);
  }, [readOnly]);

  useImperativeHandle(ref, () => ({
    getEditor: (_index) => quillRef.current,
    // Delta with the latest embed values re-read from the DOM (vitals inputs /
    // ROS contenteditable text), which Quill's own getContents() misses. Used
    // by the "Switch format" tool so flattening reflects what the user typed.
    getFreshContents: () => (quillRef.current ? getFreshDeltaWithEmbeds(quillRef.current) : null),
    // Undo/redo surfaced for the toolbar ↶ ↷ buttons in PatientHeaderBar.
    // These wrap quill.history so the buttons don't have to reach into the
    // editor internals (and keeps us free to swap the underlying mechanism
    // later — e.g. an explicit edit log for the v2 versioning system).
    undo: () => {
      try { quillRef.current?.history?.undo?.(); } catch (_) {}
    },
    redo: () => {
      try { quillRef.current?.history?.redo?.(); } catch (_) {}
    },
  }), []);

  // ---- Unresolved [edit: …] marker tracker --------------------------------
  // Master Note Accept leaves amber [edit: …] placeholders the physician must
  // resolve before the note leaves the app (Leejoe 2026-07-07: a Ctrl-F just
  // for our amber edits). Count the amber runs live; a sticky pill at the
  // top-right of the note cycles through them on click and disappears once
  // the note is clean — its presence IS the "something was not corrected"
  // signal, so it shows in read mode too.
  const [editMarkCount, setEditMarkCount] = useState(0);
  // Inline ✓/✗ chips rendered next to each amber run (Leejoe 2026-07-09:
  // "an x right next to the amber itself... leave both options" — the pill
  // serves the jump-through workflow, the chips serve scroll-and-clear).
  const [editChips, setEditChips] = useState([]);
  // Last real editor selection — buttons steal focus, so getSelection()
  // returns null right when ✕ is clicked; this remembers where the user was.
  const lastSelRef = useRef(null);

  useEffect(() => {
    let disposed = false;
    let attached = null;
    let attachTimer = null;
    let debounceTimer = null;
    const recompute = () => {
      clearTimeout(debounceTimer);
      debounceTimer = setTimeout(() => {
        if (disposed) return;
        const q = quillRef.current;
        const ranges = findAmberEditRanges(q);
        setEditMarkCount(ranges.length);
        setEditChips(buildEditChips(q, ranges));
      }, 250);
    };
    const trackSel = (range) => {
      if (range) lastSelRef.current = { index: range.index, length: range.length || 0 };
    };
    // Quill is created inside the doc-loading effect; poll until it exists
    // rather than reaching into that effect.
    // Tracks the marker auto-selected by the LAST click so a second click
    // on the same marker places the caret instead of re-selecting.
    const markerClickRef = { current: null };
    // First edit gesture RESOLVES the whole marker (Leejoe 2026-07-09):
    // typing anything or deleting any character inside an amber [edit: …]
    // run de-ambers the ENTIRE run — remaining words stay as normal text to
    // keep or edit, the count drops, ✕/jump move on. No half-amber leftovers
    // to hand-delete. Inserts must land INSIDE the run (typing just before
    // the bracket doesn't resolve it); deletes count boundaries as touching,
    // so backspacing into a marker resolves it — aggressive clearing is the
    // point.
    const resolveAmberOnEdit = (delta, oldDelta, source) => {
      const quill = quillRef.current;
      if (!quill || source !== 'user') return;
      let idx = 0;
      const touches = [];
      (delta.ops || []).forEach((op) => {
        if (op.retain) { idx += op.retain; return; }
        if (op.insert) {
          const len = typeof op.insert === 'string' ? op.insert.length : 1;
          touches.push({ from: idx, to: idx + len, ins: true });
          idx += len;
        } else if (op.delete) {
          touches.push({ from: idx, to: idx, ins: false });
          // deletes don't advance idx in post-change coordinates
        }
      });
      if (!touches.length) return;
      try {
        markerClickRef.current = null; // content shifted — reset click state
        findAmberEditRanges(quill)
          .filter((r) => touches.some((t) => (t.ins
            ? t.from < r.index + r.length && t.to > r.index
            : t.from <= r.index + r.length && t.to >= r.index)))
          .forEach((r) => {
            quill.formatText(r.index, r.length, { background: false, color: false }, 'user');
          });
      } catch (_) { /* non-fatal */ }
    };
    // Click model (Leejoe 2026-07-09): FIRST click inside an amber marker
    // selects the whole run (see it, arm ✕, or type-over everything); a
    // SECOND click on the same marker places the caret normally so part of
    // it can be edited — and the first keystroke/delete then resolves the
    // whole run via resolveAmberOnEdit.
    const selectMarkerOnClick = () => {
      const quill = quillRef.current;
      if (!quill) return;
      setTimeout(() => {
        try {
          const sel = quill.getSelection();
          if (!sel || sel.length > 0) return;
          const hit = findAmberEditRanges(quill)
            .find((r) => sel.index > r.index && sel.index < r.index + r.length);
          if (!hit) { markerClickRef.current = null; return; }
          const prev = markerClickRef.current;
          if (prev && prev.index === hit.index && prev.length === hit.length) {
            // Second click on the same marker — leave the caret where the
            // user put it.
            markerClickRef.current = null;
            return;
          }
          markerClickRef.current = { index: hit.index, length: hit.length };
          quill.setSelection(hit.index, hit.length, 'user');
        } catch (_) { /* non-fatal */ }
      }, 0);
    };
    const tryAttach = () => {
      if (disposed) return;
      const quill = quillRef.current;
      if (!quill) { attachTimer = setTimeout(tryAttach, 400); return; }
      attached = quill;
      quill.on('text-change', recompute);
      quill.on('text-change', resolveAmberOnEdit);
      quill.on('selection-change', trackSel);
      quill.root.addEventListener('click', selectMarkerOnClick);
      // Backspacing the empty line left after a hand-deleted receipt must
      // not eat the problem chip above it (Bemontez 2026-07-10: "when you
      // delete the edit the problem went away as well — forced to leave the
      // spacing"). Chips are embeds with no newline of their own, so the
      // default backspace merges into the chip. When the char before the
      // caret is a problem chip and the caret sits on an empty line, delete
      // the line's own newline instead — line gone, chip safe.
      try {
        const chipGuard = {
          key: 8,
          collapsed: true,
          handler(range) {
            try {
              const q = quillRef.current;
              if (!q || !range || range.index === 0) return true;
              const prevOp = ((q.getContents(range.index - 1, 1) || {}).ops || [])[0];
              const emb = prevOp && typeof prevOp.insert === 'object' ? prevOp.insert : null;
              const embData = emb ? emb[Object.keys(emb)[0]] : null;
              const isChip = !!(embData && (embData.displayName || embData.icd10Code));
              if (!isChip) return true;
              const curOp = ((q.getContents(range.index, 1) || {}).ops || [])[0];
              if (curOp && curOp.insert === '\n') {
                q.deleteText(range.index, 1, 'user');
                return false;
              }
              return true;
            } catch (_) { return true; }
          },
        };
        const kb = quill.keyboard;
        if (kb && kb.bindings) (kb.bindings[8] = kb.bindings[8] || []).unshift(chipGuard);
      } catch (_) { /* guard is best-effort */ }
      recompute();
    };
    tryAttach();
    return () => {
      disposed = true;
      clearTimeout(attachTimer);
      clearTimeout(debounceTimer);
      if (attached) {
        attached.off('text-change', recompute);
        attached.off('text-change', resolveAmberOnEdit);
        attached.off('selection-change', trackSel);
        try { attached.root.removeEventListener('click', selectMarkerOnClick); } catch (_) {}
      }
    };
  }, []);

  const jumpToNextEditMark = () => {
    const quill = quillRef.current;
    if (!quill) return;
    const ranges = findAmberEditRanges(quill);
    setEditMarkCount(ranges.length);
    if (!ranges.length) return;
    // Position-aware: go to the first marker BELOW the cursor/selection,
    // wrapping to the top when none remain (Leejoe 2026-07-09: jumping must
    // follow where you are in the note, not an internal list order — after
    // ✕-ing a higher marker, next = the one directly below it).
    const sel = quill.getSelection();
    const from = sel ? sel.index + (sel.length || 0) : 0;
    const r = ranges.find((x) => x.index >= from) || ranges[0];
    try { quill.setSelection(r.index, r.length, 'user'); } catch (_) {}
    try {
      // Quill only auto-scrolls its own root; our scroller is the outer
      // overflow-y-auto div, so bring the marker into view manually.
      const bounds = quill.getBounds(r.index, r.length);
      const scroller = quill.container.closest('.overflow-y-auto');
      if (scroller) {
        const offset = quill.container.getBoundingClientRect().top
          - scroller.getBoundingClientRect().top + scroller.scrollTop;
        scroller.scrollTo({
          top: Math.max(0, offset + bounds.top - scroller.clientHeight / 3),
          behavior: 'smooth',
        });
      }
    } catch (_) {}
  };

  // ---- Edit-aware ✓/✗ actions (Task #290 v1, Leejoe 2026-07-09) ------------
  // ✗ means "reject this edit", and the [edit: …] tag grammar (Master Note
  // rules 8/9) says what that means: an added-problem tag rejects by removing
  // the WHOLE problem — header, tag, plan, and its "(for # name)" order
  // lines; a was:-tag rejects by restoring the old header it carries; any
  // other marker rejects by deleting the marker text. ✓ means "keep it":
  // content stays, the receipt line (or just the amber) goes.
  const ensureMarkerEditable = () => {
    const quill = quillRef.current;
    if (!quill) return null;
    if (typeof quill.isEnabled === 'function' && !quill.isEnabled()) {
      window.alert('The note is in read mode — click Edit in the header first.');
      return null;
    }
    return quill;
  };

  // Chips capture ranges at render time; the doc may have shifted since.
  // Re-anchor to the live amber run overlapping the stored one, or bail.
  const resolveLiveRange = (quill, stored) => findAmberEditRanges(quill)
    .find((r) => r.index < stored.index + stored.length && r.index + r.length > stored.index) || null;

  // ✓ NEVER deletes CONTENT (Leejoe 2026-07-09: "when you press check make
  // sure they stay please!") — headers, chips, and plans are untouchable.
  // A was:/added receipt line, though, is pure bookkeeping: accepting removes
  // it whole (chip-aware line model guarantees the tag's OWN line only —
  // pics 1-2 7:36 PM: leaving the receipt as plain text just made the
  // physician hand-delete it). Plain markers de-amber and keep their text.
  // Pin the outer scroller across a ✓/✗ mutation — the reader should watch
  // the change happen in place, not get thrown to the top of the section
  // (Leejoe 2026-07-09: "disorienting to keep popping up and scrolling down").
  const pinScrollAfter = (quill) => {
    const scroller = quill.container && quill.container.closest('.overflow-y-auto');
    const saved = scroller ? scroller.scrollTop : null;
    return () => {
      if (scroller && saved != null) {
        requestAnimationFrame(() => { try { scroller.scrollTop = saved; } catch (_) {} });
      }
    };
  };

  const acceptEditMark = (stored, { advance = false } = {}) => {
    const quill = ensureMarkerEditable();
    if (!quill) return;
    const range = resolveLiveRange(quill, stored);
    if (!range) return;
    const model = docModel(quill);
    const cls = classifyEditMark(model.s.slice(range.index, range.index + range.length));
    // Advancing to the next edit means we WANT to move — don't pin the scroll
    // (Leejoe 2026-07-13: ✓ should jump to the next edit like the pill ✗ does).
    const restoreScroll = advance ? null : pinScrollAfter(quill);
    try {
      if (cls === 'was' || cls === 'added') {
        const li = lineIndexFor(model, range.index);
        const line = li !== -1 ? model.lines[li] : null;
        // Only take the line when the receipt owns it — never a shared line.
        if (line && !line.chip && line.text.trim().startsWith('[edit:')) {
          deleteLineWithGapCollapse(quill, model, line);
          if (restoreScroll) restoreScroll();
          if (advance) setTimeout(jumpToNextEditMark, 0);
          return;
        }
      }
      quill.formatText(range.index, range.length, { background: false, color: false }, 'user');
    } catch (_) { /* non-fatal */ }
    if (restoreScroll) restoreScroll();
    if (advance) setTimeout(jumpToNextEditMark, 0);
  };

  // Delete a full line and, if that leaves two blank lines touching, collapse
  // one — no weird gaps left where the amber was (Leejoe 2026-07-09).
  const deleteLineWithGapCollapse = (quill, model, line) => {
    let { start, end } = line;
    const li = model.lines.indexOf(line);
    const prev = li > 0 ? model.lines[li - 1] : null;
    const next = li >= 0 && li < model.lines.length - 1 ? model.lines[li + 1] : null;
    const isBlank = (L) => !!L && !L.chip && L.text.trim() === '';
    if (isBlank(prev) && isBlank(next)) end = next.end; // take one blank neighbor too
    quill.deleteText(start, end - start, 'user');
    return start;
  };

  const rejectEditMark = (stored, { advance = false } = {}) => {
    const quill = ensureMarkerEditable();
    if (!quill) return;
    const range = resolveLiveRange(quill, stored);
    if (!range) return;
    const model = docModel(quill);
    const text = model.s.slice(range.index, range.index + range.length);
    const cls = classifyEditMark(text);
    // Pill-✗ advances on purpose; the inline ✗ stays put so the physician
    // watches the problem leave in place.
    const restoreScroll = advance ? null : pinScrollAfter(quill);
    try {
      if (cls === 'added') {
        const block = problemBlockForTag(model, range);
        if (block) {
          const name = problemNameFromHeader(block.headerLine);
          // Problem block + every order line tagged "(for # name)" go
          // together — that is exactly what rule 9's provenance tags are
          // for. Delete bottom-up so earlier ranges stay valid.
          const doomed = [{ start: block.start, end: block.end }]
            .concat(orderLineRangesFor(model, name))
            .sort((a, b) => b.start - a.start);
          doomed.forEach((d) => quill.deleteText(d.start, d.end - d.start, 'user'));
          try { quill.setSelection(Math.min(block.start, Math.max(0, quill.getLength() - 1)), 0, 'silent'); } catch (_) {}
        } else {
          quill.deleteText(range.index, range.length, 'user');
        }
      } else if (cls === 'was') {
        const m = text.match(/\[edit:\s*was:\s*([^\]]+)\]/i);
        const oldHeader = m ? m[1].trim() : null;
        const block = oldHeader ? problemBlockForTag(model, range) : null;
        const tagLi = lineIndexFor(model, range.index);
        if (block && tagLi !== -1) {
          const tagLine = model.lines[tagLi];
          const header = block.headerLine;
          // Bottom-up: drop the tag line, then swap the renamed header back
          // to the old one the tag carried. A chip header is a single-char
          // block embed — replace it with a plain-text '# Old Name' line.
          deleteLineWithGapCollapse(quill, model, tagLine);
          // Restored headers must LOOK like problem headers — dark bold, like
          // every other problem (pic 4 7:37 PM: '# DM' came back as plain
          // body text).
          const headerFmt = { bold: true };
          if (header.chip) {
            quill.deleteText(header.start, 1, 'user');
            quill.insertText(header.start, `${oldHeader}\n`, headerFmt, 'user');
          } else {
            quill.deleteText(header.start, (header.end - 1) - header.start, 'user');
            quill.insertText(header.start, oldHeader, headerFmt, 'user');
          }
        } else {
          quill.deleteText(range.index, range.length, 'user');
        }
      } else {
        // Plain marker: if it owns its whole line take the line (plus any
        // doubled blank), else just the run — no husks, no gaps.
        const li = lineIndexFor(model, range.index);
        const line = li !== -1 ? model.lines[li] : null;
        const ownsLine = !!line && !line.chip && line.text.trim() === text.trim();
        let caret = range.index;
        if (ownsLine) caret = deleteLineWithGapCollapse(quill, model, line);
        else quill.deleteText(range.index, range.length, 'user');
        try { quill.setSelection(caret, 0, 'silent'); } catch (_) {}
      }
    } catch (_) { /* non-fatal */ }
    if (restoreScroll) restoreScroll();
    if (advance) setTimeout(jumpToNextEditMark, 0);
  };

  // Pill-adjacent ✕: reject the marker the user is on (falling back to the
  // last real selection — clicking the button blurs the editor) and advance.
  // Not on one? Jump first so nothing is rejected sight-unseen.
  const rejectCurrentEditMark = () => {
    const quill = ensureMarkerEditable();
    if (!quill) return;
    const ranges = findAmberEditRanges(quill);
    setEditMarkCount(ranges.length);
    if (!ranges.length) return;
    const sel = quill.getSelection() || lastSelRef.current;
    const hit = sel
      ? ranges.find((r) => sel.index <= r.index + r.length && sel.index + (sel.length || 0) >= r.index)
      : null;
    if (!hit) { jumpToNextEditMark(); return; }
    rejectEditMark(hit, { advance: true });
  };

  // Pill-adjacent ✓: accept (keep) the marker the user is on and advance — the
  // top-bar twin of the ✗, so you can walk edits from the pill without touching
  // each line. The inline ✓ still accepts in place; this one jumps.
  const acceptCurrentEditMark = () => {
    const quill = ensureMarkerEditable();
    if (!quill) return;
    const ranges = findAmberEditRanges(quill);
    setEditMarkCount(ranges.length);
    if (!ranges.length) return;
    const sel = quill.getSelection() || lastSelRef.current;
    const hit = sel
      ? ranges.find((r) => sel.index <= r.index + r.length && sel.index + (sel.length || 0) >= r.index)
      : null;
    if (!hit) { jumpToNextEditMark(); return; }
    acceptEditMark(hit, { advance: true });
  };

  // We deliberately keep the host element mounted even while `doc` is null
  // (the brief window during a patient switch). If we returned early here,
  // Quill's container would be detached and the re-mount logic above wouldn't
  // fire until the next text-change — the document would visually disappear.
  return (
    <div className="flex-1 overflow-y-auto">
      {/* Sticky, height-zero bar so the pill pins to the top-right of the
          note viewport without pushing content down. pointer-events pass
          through everywhere except the pill itself. */}
      <div className="pointer-events-none sticky top-3 z-20 flex h-0 select-none justify-end pr-5">
        {editMarkCount > 0 && (
          <>
            <button
              type="button"
              onClick={jumpToNextEditMark}
              className="pointer-events-auto inline-flex items-center gap-1.5 rounded-full border border-amber-400 bg-amber-100 px-3 py-1 text-xs font-bold text-amber-900 shadow-md hover:bg-amber-200"
              title="Unresolved [edit: …] markers from Master Note — click to jump to each one; this pill disappears when the note is clean"
              aria-label="Jump to next unresolved edit marker"
            >
              ✎ {editMarkCount} edit{editMarkCount === 1 ? '' : 's'} left
            </button>
            <button
              type="button"
              onClick={acceptCurrentEditMark}
              className="pointer-events-auto ml-1.5 inline-flex items-center rounded-full border border-amber-400 bg-amber-100 px-2.5 py-1 text-xs font-bold text-amber-900 shadow-md hover:border-emerald-400 hover:bg-emerald-100 hover:text-emerald-700"
              title="Accept (keep) the edit you're on and jump to the next. Not on one? Jumps first so you see it before accepting."
              aria-label="Accept current edit and jump to next"
            >
              ✓
            </button>
            <button
              type="button"
              onClick={rejectCurrentEditMark}
              className="pointer-events-auto ml-1.5 inline-flex items-center rounded-full border border-amber-400 bg-amber-100 px-2.5 py-1 text-xs font-bold text-amber-900 shadow-md hover:border-red-400 hover:bg-red-100 hover:text-red-700"
              title="Reject the edit you're on and jump to the next — an AI-added problem is removed whole (header, plan, and its orders); a renamed problem gets its old name back; a plain marker is deleted. Not on one? Jumps first so you see it before rejecting."
              aria-label="Reject current edit and jump to next"
            >
              ✕
            </button>
          </>
        )}
      </div>
      <div className="max-w-3xl mx-auto px-8 py-8">
        {title && (
          <div className="flex items-baseline gap-3 mb-1">
            <h1 className="text-3xl font-bold tracking-tight text-slate-900 dark:text-slate-100">{title}</h1>
          </div>
        )}
        {subtitle && <div className="text-sm text-slate-500 dark:text-slate-400 mb-6">{subtitle}</div>}
        <div className="h-px bg-slate-200 dark:bg-slate-800 mb-7" />
        <AiActionToolbar
          getText={() => {
            const q = quillRef.current;
            return q ? q.getText() : '';
          }}
          insertText={(t) => {
            const q = quillRef.current;
            if (!q || !t) return;
            const sel = q.getSelection(true) || { index: q.getLength(), length: 0 };
            // Tag the inserted text with a trailing newline so it lands as its
            // own paragraph instead of merging into whatever line the cursor
            // happened to be on.
            q.insertText(sel.index, t + '\n', 'user');
            q.setSelection(sel.index + t.length + 1, 0);
          }}
        />
        <div className="relative">
          <div data-quill-host ref={quillHostRef} className="prose max-w-none text-slate-700 dark:text-slate-200" />
          {/* Inline ✓/✗ chips at the end of each amber run. Positioned from
              quill.getBounds (container-relative), so they scroll with the
              text — no scroll listener needed. Actions re-anchor to the live
              range at click time (resolveLiveRange), so a stale 250ms-debounced
              position can shift a chip visually but never corrupts an action. */}
          {editChips.map((c) => (
            <span
              key={c.key}
              className="absolute z-10 inline-flex items-center gap-1"
              style={{ top: c.top - 1, left: c.left + 8 }}
            >
              <button
                type="button"
                onClick={() => acceptEditMark(c.range)}
                className="inline-flex h-5 w-5 items-center justify-center rounded-full border border-emerald-400 bg-white text-[11px] font-bold text-emerald-700 shadow-sm hover:bg-emerald-100 dark:bg-slate-800 dark:hover:bg-emerald-900"
                title={c.cls === 'added' ? 'Keep this AI-added problem — the [edit:] receipt line is removed, everything else stays'
                  : c.cls === 'was' ? 'Keep the new name — the [edit:] receipt line is removed, everything else stays'
                  : 'Mark handled — clears the highlight, keeps the text'}
                aria-label="Accept this edit"
              >
                ✓
              </button>
              <button
                type="button"
                onClick={() => rejectEditMark(c.range)}
                className="inline-flex h-5 w-5 items-center justify-center rounded-full border border-red-300 bg-white text-[11px] font-bold text-red-600 shadow-sm hover:bg-red-100 dark:bg-slate-800 dark:hover:bg-red-900"
                title={c.cls === 'added' ? 'Remove this problem — header, plan, and its (for # …) orders'
                  : c.cls === 'was' ? 'Restore the old problem name'
                  : 'Delete this marker'}
                aria-label="Reject this edit"
              >
                ✕
              </button>
            </span>
          ))}
        </div>
      </div>
    </div>
  );
});

// Scan the document for amber [edit: …] runs — the background the Master
// Note Accept transform applies (same '#fde68a' literal as
// MedStudentClinicPage). Consecutive amber ops merge into one range so a
// marker split across ops counts once; embeds break a run.
const EDIT_MARK_BG = '#fde68a';
function findAmberEditRanges(quill) {
  if (!quill) return [];
  const ops = (quill.getContents() || {}).ops || [];
  const ranges = [];
  let idx = 0;
  let open = null;
  for (const op of ops) {
    const len = typeof op.insert === 'string' ? op.insert.length : 1;
    const amber = typeof op.insert === 'string'
      && !!(op.attributes && op.attributes.background === EDIT_MARK_BG);
    if (amber) {
      if (open) open.length += len;
      else open = { index: idx, length: len };
    } else if (open) {
      ranges.push(open);
      open = null;
    }
    idx += len;
  }
  if (open) ranges.push(open);
  return ranges;
}

// ---- Edit-aware marker helpers (Task #290 v1.2) -----------------------------
// All index math runs on a doc-aligned string: embeds (ICD chips, images)
// count as ONE character exactly like Quill's document model, so ranges from
// findAmberEditRanges and Quill APIs line up. quill.getText() would silently
// drop embeds and shift every index after a chip.
//
// CRITICAL (Holguin 2026-07-09 "leaving NEITHER" bug): coded problem headers
// are icdProblemBlot BLOCK embeds — one embed char with NO trailing newline
// of its own. Newline-only line math fused the chip with the [edit:] tag
// beneath it into one "line", so deleting the tag line deleted the header
// chip too. The line model below treats a problem-chip embed as its own line.
const EMBED_CHAR = '￼';

function docCharString(quill) {
  const ops = (quill.getContents() || {}).ops || [];
  let s = '';
  for (const op of ops) s += typeof op.insert === 'string' ? op.insert : EMBED_CHAR;
  return s;
}

// Doc model: char string + line list where an ICD-problem chip is a standalone
// header line carrying its displayName/icd10Code (for orders-sweep matching).
function docModel(quill) {
  const ops = (quill.getContents() || {}).ops || [];
  let s = '';
  const chipAt = {};
  for (const op of ops) {
    if (typeof op.insert === 'string') { s += op.insert; continue; }
    const key = op.insert ? Object.keys(op.insert)[0] : null;
    const data = key ? op.insert[key] : null;
    if (data && (data.displayName || data.icd10Code)) {
      chipAt[s.length] = { displayName: String(data.displayName || ''), icd10Code: String(data.icd10Code || '') };
    }
    s += EMBED_CHAR;
  }
  const lines = [];
  let start = 0;
  const pushText = (a, b) => { if (b > a) lines.push({ start: a, end: b, text: s.slice(a, b).replace(/\n$/, ''), chip: null }); };
  for (let i = 0; i < s.length; i += 1) {
    if (chipAt[i] !== undefined) {
      pushText(start, i);
      lines.push({ start: i, end: i + 1, text: EMBED_CHAR, chip: chipAt[i] });
      start = i + 1;
    } else if (s[i] === '\n') {
      pushText(start, i + 1);
      start = i + 1;
    }
  }
  pushText(start, s.length);
  return { s, lines };
}

function lineIndexFor(model, docIndex) {
  return model.lines.findIndex((L) => docIndex >= L.start && docIndex < L.end);
}

// Master Note rule-8 tag grammar. 'folded in:' / 'split from:' need the
// engine to reconstruct (v1.5) — until then they behave like plain markers.
function classifyEditMark(text) {
  const t = text || '';
  if (!/\[edit:/i.test(t)) return 'plain';
  if (/\[edit:\s*added/i.test(t)) return 'added';
  if (/\[edit:\s*was:/i.test(t)) return 'was';
  return 'plain';
}

// A problem header is either an ICD chip line or a plain-text '# Name' line.
function isProblemHeaderLine(line) {
  if (line.chip) return true;
  return /^\s*#\s+\S/.test((line.text || '').replace(new RegExp(EMBED_CHAR, 'g'), ''));
}

// ALL-CAPS template headers ('ORDERS', 'SCORES / RATING SCALES') bound a
// problem block from below just like the next '#' problem does.
function isSectionHeaderLine(line) {
  if (line.chip) return false;
  const t = (line.text || '').replace(new RegExp(EMBED_CHAR, 'g'), '').trim();
  return t.length >= 4 && /^[A-Z][A-Z0-9 &/()\-:.]*$/.test(t) && /[A-Z]{3}/.test(t);
}

function problemNameFromHeader(line) {
  if (line.chip) return (line.chip.displayName || '').trim();
  return (line.text || '')
    .replace(new RegExp(EMBED_CHAR, 'g'), '')
    .replace(/^\s*#\s*/, '')
    .replace(/\s*\([A-Z][0-9][0-9A-Z.]*\)\s*$/, '') // trailing ICD code
    .trim();
}

// The problem block an [edit: …] tag governs: from its header line (chip or
// '#' text — the nearest one above the tag, per rule 8 "directly beneath")
// down to — not including — the next problem or section header.
function problemBlockForTag(model, tagRange) {
  const li = lineIndexFor(model, tagRange.index);
  if (li === -1) return null;
  let header = null;
  for (let i = li; i >= 0; i--) {
    if (isProblemHeaderLine(model.lines[i])) { header = model.lines[i]; break; }
    if (i !== li && isSectionHeaderLine(model.lines[i])) return null; // crossed a section
  }
  if (!header) return null;
  let end = model.s.length;
  for (let i = li + 1; i < model.lines.length; i++) {
    if (isProblemHeaderLine(model.lines[i]) || isSectionHeaderLine(model.lines[i])) {
      end = model.lines[i].start;
      break;
    }
  }
  return { start: header.start, end, headerLine: header };
}

// Rule-9 provenance sweep: every order line ending "(for # <name>)" belongs
// to the problem being removed.
function orderLineRangesFor(model, problemName) {
  const name = (problemName || '').trim().toLowerCase();
  if (!name) return [];
  const needle = `(for # ${name}`;
  return model.lines
    .filter((L) => !L.chip && L.text.toLowerCase().includes(needle))
    .map((L) => ({ start: L.start, end: L.end }));
}

// Chip geometry for the inline ✓/✗ pair: horizontally pinned to the right
// margin next to the pencil; vertically anchored to WHAT IS BEING JUDGED.
// For was/added receipts that is the PROPOSED problem header above the tag,
// not the receipt itself (Leejoe 2026-07-09: "a check and the x should be
// next to what you're actually accepting or disagreeing with — I'm not
// checking yes to # OSA, I'm checking yes to the proposal"). Plain markers
// anchor to their own line.
function buildEditChips(quill, ranges) {
  if (!quill || !ranges.length) return [];
  const model = docModel(quill);
  const width = (quill.container && quill.container.clientWidth) || 700;
  // Just LEFT of the pencil icon, not on top of it (Leejoe pic 8:01 PM).
  const left = Math.max(60, width - 100);
  const chips = [];
  ranges.forEach((r, i) => {
    const cls = classifyEditMark(model.s.slice(r.index, r.index + r.length));
    let anchorIndex = r.index + r.length;
    if (cls === 'was' || cls === 'added') {
      const block = problemBlockForTag(model, { index: r.index, length: r.length });
      if (block) anchorIndex = block.start;
    }
    let b;
    try { b = quill.getBounds(anchorIndex, 0); } catch (_) { return; }
    if (!b) return;
    chips.push({
      key: `${r.index}-${r.length}-${i}`,
      cls,
      range: { index: r.index, length: r.length },
      top: b.top,
      left,
    });
  });
  return chips;
}

export default DocumentBody;
